import JsCookie from "js-cookie";

import Codec from "@repo/utils/Codec";
import Json from "@repo/utils/Json";
import Log from "@repo/utils/Log";
import UError from "@repo/utils/UError";
import UFunction from "@repo/utils/UFunction";
import UProxy from "@repo/utils/UProxy";

const createWebStorageImpl = (() => {
  const clientByType = {
    auto: UFunction.memo(() => {
      try {
        localStorage.setItem("test", "test");
        localStorage.removeItem("test");
        return localStorage;
      } catch (e) {
        return sessionStorage;
      }
    }),
    localStorage: () => localStorage,
    sessionStorage: () => sessionStorage,
  } satisfies Record<"auto" | "localStorage" | "sessionStorage", () => Storage>;

  return UFunction.memo(
    (client: "auto" | "localStorage" | "sessionStorage") => {
      // Fall back to sessionStorage in private browser sessions that disable
      // localStorage
      const webStorage = clientByType[client];

      // js-cookie will encode invalid characters to be valid cookie name
      // characters, don't need to handle on our side
      const key = (ns: string, path: PropertyKey[]) =>
        `${ns}:${JSON.stringify(path)}`;

      return {
        name: {
          auto: "WebStorage",
          localStorage: "LocalStorage",
          sessionStorage: "SessionStorage",
        }[client],
        key: (ns, path) => () => key(ns, path),
        get: (ns, path) => async () => {
          try {
            const encoded = webStorage().getItem(key(ns, path));
            if (!encoded) return;
            return JSON.parse(encoded);
          } catch (e) {
            return;
          }
        },
        set: (ns, path) => async (value) => {
          await webStorage().setItem(key(ns, path), JSON.stringify(value));
          return value;
        },
        delete: (ns, path) => async () => {
          await webStorage().removeItem(key(ns, path));
        },
      } satisfies Storage.Impl;
    }
  );
})();

namespace Storage {
  declare const value: unique symbol;
  export type Value<T> = { [value]: T };
  export namespace Value {
    export type Type<T> =
      T extends Value<infer V> ? V : T extends Operations<infer V> ? V : never;
  }

  export type Operations<T = any, SetOptions = {}, DeleteOptions = {}> = {
    key: () => string;
    get: () => Promise<T | undefined>;
    set: (value: T, options?: SetOptions) => Promise<T>;
    delete: (options?: DeleteOptions) => Promise<void>;
  };

  export type Impl<SetOptions = {}, DeleteOptions = {}> = {
    [K in keyof Operations]: (
      ns: string,
      path: PropertyKey[]
    ) => Operations<any, SetOptions, DeleteOptions>[K];
  } & { name: string };
  export namespace Impl {
    export const WebStorage = () => createWebStorageImpl("auto");
    export const SessionStorage = () => createWebStorageImpl("sessionStorage");
    export const LocalStorage = () => createWebStorageImpl("localStorage");

    export const Cookies = UFunction.memo(
      UFunction.inferParams(
        ({
          // Use raw if cookie name is used externally and cannot be transformed
          keyFormat = "default" as "default" | "raw",
          codec = Json.codec as Codec<any, string>,
          defaultAttributes = {} as Cookies.CookieAttributes,
        } = {}): Storage.Impl<
          Cookies.CookieAttributes,
          Cookies.CookieAttributes
        > => {
          const key = (ns: string, path: PropertyKey[]) => {
            if (keyFormat === "default") return `${ns}:${JSON.stringify(path)}`;
            if (keyFormat === "raw") return path.join(".");
            throw new UError.Unreachable();
          };

          return {
            name: "Cookies",
            key: (ns, path) => () => key(ns, path),
            get: (ns, path) => async () => {
              try {
                const encoded = JsCookie.get(key(ns, path));
                if (!encoded) return;
                return codec.decode(encoded);
              } catch (e) {
                return;
              }
            },
            set:
              (ns, path) =>
              async (value, options?: Cookies.CookieAttributes) => {
                JsCookie.set(key(ns, path), codec.encode(value), {
                  ...defaultAttributes,
                  ...options,
                });
                return value;
              },
            delete: (ns, path) => async (options?: Cookies.CookieAttributes) =>
              JsCookie.remove(key(ns, path), {
                ...defaultAttributes,
                ...options,
              }),
          };
        }
      )
    );
  }

  export type Client<T, SetOptions, DeleteOptions> = (T extends Value<infer V>
    ? Operations<V, SetOptions, DeleteOptions>
    : {}) & {
    [K in keyof Omit<T, keyof Value<any>>]: Client<
      T[K],
      SetOptions,
      DeleteOptions
    >;
  };
  export namespace Client {
    export type Definition = Value<any> | { [key: string]: Definition };
  }
  export const defineClient = <T extends Client.Definition>() => ({
    create: <SetOptions = never, DeleteOptions = never>(
      ns: string,
      impl: Impl<SetOptions, DeleteOptions>
    ) =>
      UProxy.createPathProxy((path) => (...args) => {
        const op = path.pop()!;
        const res = impl[op as keyof Operations](
          ns,
          path
        )(
          // @ts-expect-error
          ...args
        );
        if (op !== "get" && op !== "key")
          Log.withTrace`${Log.fn([`${impl.name}[${ns}]`, ...path], args)}: ${res}`;
        return res;
      }) as unknown as Client<T, SetOptions, DeleteOptions>,
  });
}

export default Storage;
